if (!customElements.get('sticky-atc-panel')) {
  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.addToCartButton = this.findMainButton();

      this._observer = null;
      this._mutationObserver = null;
      this.buttonVisible = true;
      this.lastScrollY = window.scrollY || 0;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.updateStickyState = this.updateStickyState.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      } else {
        window.addEventListener('scroll', this.onScroll);
        window.addEventListener('resize', this.onResize);
        this.updateStickyState();
      }

      // Listen for variant changes
      document.addEventListener('variant:change', this.onVariantChange);
      document.addEventListener('variant:changed', this.onVariantChange);
      document.addEventListener('on:variant:change', this.onVariantChange);
    }

    connectedCallback() {
      // Initialize price on first load to match currently selected variant
      // Use setTimeout to ensure variant-picker is fully initialized
      setTimeout(() => {
        this.onVariantChange();
      }, 100);
    }

    findMainButton() {
      if (!this.productSection) {
        return document.querySelector('button[type="submit"][name="add"].w-full') ||
               document.querySelector('button[type="submit"][name="add"]') ||
               document.querySelector('[name="add"]');
      }

      return this.productSection.querySelector('button[type="submit"][name="add"].w-full') ||
             this.productSection.querySelector('button[type="submit"][name="add"]') ||
             this.productSection.querySelector('[name="add"]');
    }

    bindEvents() {
      this.addToCartButton = this.findMainButton();

      if (this._observer) {
        try { this._observer.disconnect(); } catch (e) {}
        this._observer = null;
      }

      if (this.addToCartButton) {
        this._observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target !== this.addToCartButton) return;
            this.buttonVisible = entry.isIntersecting && entry.intersectionRatio > 0;
            this.updateStickyState();
          });
        }, {
          root: null,
          threshold: 0.01
        });
        try { this._observer.observe(this.addToCartButton); } catch (e) {}
      }

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      if (this._mutationObserver) {
        try { this._mutationObserver.disconnect(); } catch (e) {}
      }
      this._mutationObserver = new MutationObserver(this.handleMutations);
      this._mutationObserver.observe(this.productSection || document.body, { childList: true, subtree: true });

      this.updateStickyState();
    }

    handleMutations() {
      const prev = this.addToCartButton;
      this.addToCartButton = this.findMainButton();
      if (this.addToCartButton !== prev) {
        this.bindEvents();
      } else {
        this.updateStickyState();
      }
    }

    onScroll() {
      this.lastScrollY = window.scrollY || 0;
      StickyAtcPanel.handleScroll();
      this.updateStickyState();
    }

    onResize() {
      this.updateStickyState();
    }

    isButtonVisible(btn) {
      if (!btn || !btn.isConnected) return false;
      const style = getComputedStyle(btn);
      if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) return false;
      const vh = window.visualViewport ? window.visualViewport.height : window.innerHeight;
      const rect = btn.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < vh;
    }

    updateStickyState() {
      if (!this.productSection) {
        if (!this.buttonVisible) this.classList.remove('sticky-atc-panel--out');
        else this.classList.add('sticky-atc-panel--out');
        return;
      }

      if (!this.addToCartButton) {
        const scrolledPastProduct = window.scrollY > (this.productSection.getBoundingClientRect().top + window.scrollY + 20);
        const shouldShowFallback = !this.buttonVisible && scrolledPastProduct;
        if (shouldShowFallback) this.classList.remove('sticky-atc-panel--out'); else this.classList.add('sticky-atc-panel--out');
        return;
      }

      const btnRect = this.addToCartButton.getBoundingClientRect();
      const btnBottomPage = btnRect.bottom + window.scrollY;
      const btnTopPage = btnRect.top + window.scrollY;

      const visibleNow = this.isButtonVisible(this.addToCartButton);

      this.buttonVisible = visibleNow;

      const scrolledPastBtn = btnRect.top < 0;
      const scrolledBelowBtn = btnRect.top >= (window.visualViewport ? window.visualViewport.height : window.innerHeight);

      const scrolledPastProduct = window.scrollY > (this.productSection.getBoundingClientRect().top + window.scrollY + 20);

      const shouldShow = (!visibleNow && scrolledPastBtn) || (!visibleNow && scrolledPastProduct && scrolledBelowBtn && window.scrollY > btnBottomPage);

      if (shouldShow) this.classList.remove('sticky-atc-panel--out'); else this.classList.add('sticky-atc-panel--out');
    }

    onVariantChange(event) {
      try {
        // Get the variant picker to access variant data
        const variantPicker = this.productSection?.querySelector('variant-picker');
        if (!variantPicker || !variantPicker.variant) return;

        const variant = variantPicker.variant;
        const formatted = variantPicker.data?.formatted?.[String(variant.id)] || {};

        // Update the sticky panel price
        const stickyPrice = this.querySelector('.sticky-atc__price');
        if (!stickyPrice) return;

        const priceCurrentEl = stickyPrice.querySelector('.price__current');
        const priceWasEl = stickyPrice.querySelector('.price__was');
        const unitPriceEl = stickyPrice.querySelector('.unit-price');

        if (!priceCurrentEl) return;

        const currentValueEl = priceCurrentEl.querySelector('.js-value');
        const wasValueEl = priceWasEl?.querySelector('.js-value');

        // Update sale/regular price
        if (variant.compare_at_price > variant.price) {
          // On sale
          if (currentValueEl) currentValueEl.innerHTML = formatted.price || '';
          if (wasValueEl) wasValueEl.innerHTML = formatted.compareAtPrice || '';
          stickyPrice.classList.add('price--on-sale');
        } else {
          // Regular price
          if (currentValueEl) currentValueEl.innerHTML = formatted.price || '';
          if (wasValueEl) wasValueEl.innerHTML = '';
          stickyPrice.classList.remove('price--on-sale');
        }

        // Update unit price if present
        if (variant.unit_price_measurement && unitPriceEl) {
          const valueEl = unitPriceEl.querySelector('.unit-price__price');
          const unitEl = unitPriceEl.querySelector('.unit-price__unit');
          const value = variant.unit_price_measurement.reference_value;
          const unit = variant.unit_price_measurement.reference_unit;

          if (valueEl) valueEl.innerHTML = formatted.unitPrice || '';
          if (unitEl) unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else if (unitPriceEl) {
          unitPriceEl.hidden = true;
        }

        // Update availability state
        stickyPrice.classList.toggle('price--sold-out', !variant.available);

        // Update the hidden variant ID input in the sticky form
        const stickyForm = this.querySelector('.js-product-form-sticky');
        const variantInput = stickyForm?.querySelector('input[name="id"]');
        if (variantInput) {
          variantInput.value = variant.id;
        }

        // Update the button state using same logic as variant-picker
        this.updateStickyButton(variant);
      } catch (error) {
        console.error('Error updating sticky panel:', error);
      }
    }

    updateStickyButton(variant) {
      try {
        const stickyForm = this.querySelector('.js-product-form-sticky');
        if (!stickyForm) return;

        const submitButton = stickyForm.querySelector('button[name="add"]');
        if (!submitButton) return;

        // Check badge data to see if variant should be disabled
        const variantData = window.variantMetafieldData?.[variant.id];
        const badge = variantData?.badge_value || variantData?.badge || variantData?.Badge || variantData?.badge_label || null;
        const shouldDisableByBadge = badge && ['out of stock', 'discontinued'].includes(badge.toLowerCase());

        // Determine button state
        const isAvailable = variant.available && !shouldDisableByBadge;
        const labelOut = theme.strings?.noStock || 'Sold Out';
        const labelAdd = submitButton.getAttribute('data-add-to-cart-text') || 'Add to cart';

        // Update button
        submitButton.disabled = !isAvailable;
        submitButton.style.opacity = isAvailable ? '1' : '0.5';
        submitButton.style.pointerEvents = isAvailable ? 'auto' : 'none';
        submitButton.textContent = isAvailable ? labelAdd : labelOut;

        // Store original text if not already stored
        if (!isAvailable && !submitButton.dataset.originalText) {
          submitButton.dataset.originalText = labelAdd;
        } else if (isAvailable && submitButton.dataset.originalText) {
          delete submitButton.dataset.originalText;
        }
      } catch (error) {
        console.error('Error updating sticky button:', error);
      }
    }


    disconnectedCallback() {
      try { window.removeEventListener('scroll', this.onScroll); } catch(e) {}
      try { window.removeEventListener('resize', this.onResize); } catch(e) {}
      try { document.removeEventListener('variant:change', this.onVariantChange); } catch(e) {}
      try { document.removeEventListener('variant:changed', this.onVariantChange); } catch(e) {}
      try { document.removeEventListener('on:variant:change', this.onVariantChange); } catch(e) {}
      if (this._observer) {
        try { this._observer.disconnect(); } catch(e) {}
      }
      if (this._mutationObserver) {
        try { this._mutationObserver.disconnect(); } catch(e) {}
      }
    }

    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
